import { PaginationSize } from "@/common/enum/pagination-size";
import { Footnote12Description } from "@/components/typography/Footnote12Description";
import { BasePageDto } from "@/types/dto/base-page-dto";
import { ItemDetailDto } from "@/types/dto/item-detail-dto";
import { TableHeader } from "@/types/table-header";
import { Table, Checkbox } from "flowbite-react";
import { CustomCell } from "../CustomCell";
import { ItemMainTableFooter } from "./ItemMainTableFooter";
import { ItemMainTableRow } from "./ItemMainTableRow";
import { useRecoilState } from "recoil";
import { selectedItemsState } from "@/atoms/item/selected-items-state";
import { useEffect } from "react";
import { ItemAdminOrderTypeExpanded } from "@/common/enum/item-admin-order-type";
import { ChevronUpOutlineIcon } from "@/components/icon/ChevronUpOutlineIcon";
import { ChevronDownOutlineIcon } from "@/components/icon/ChevronDownOutlineIcon";
import { HorizontalLineIcon } from "@/components/icon/HorizontalLineIcon";

interface Props {
  itemPage: BasePageDto<ItemDetailDto>;
  handleItemNameClick: (id: number) => void;
  handlePageChange: (currentPage: number) => void;
  itemSortType: ItemAdminOrderTypeExpanded;
  handleSortChange: (sortType: ItemAdminOrderTypeExpanded) => void;
  refreshData?: () => void;
}

interface SortableTableHeader extends TableHeader {
  key: keyof typeof ItemAdminOrderTypeExpanded;
  sortable: boolean;
}

const headers: SortableTableHeader[] = [
  {
    title: "선택",
    width: "w-[50px]",
    align: "flex justify-center",
    key: "ALL",
    sortable: false,
  },
  {
    title: "No",
    width: "w-[56px]",
    align: "flex justify-center",
    key: "ALL",
    sortable: false,
  },
  {
    title: "카테고리",
    width: "w-[156px]",
    key: "CATEGORY",
    sortable: true,
  },
  {
    title: "상품명",
    width: "w-[647px]",
    key: "NAME",
    sortable: true,
  },
  {
    title: "최소 발주 수량",
    width: "w-[120px]",
    key: "ALL",
    sortable: false,
  },
  {
    title: "판매가 (VAT 포함)",
    width: "w-[120px]",
    key: "ALL",
    sortable: false,
  },
  {
    title: "상품 할인",
    width: "w-[120px]",
    key: "ALL",
    sortable: false,
  },
  {
    title: "최종 판매가 (VAT 포함)",
    width: "w-[87px]",
    align: "flex justify-center",
    key: "ALL",
    sortable: false,
  },
  {
    title: "판매 여부",
    width: "w-[115px]",
    align: "flex justify-center",
    key: "IS_FOR_SALE",
    sortable: true,
  },
  {
    title: "진열 여부",
    width: "w-[115px]",
    align: "flex justify-center",
    key: "IS_DISPLAYED",
    sortable: true,
  },
  {
    title: "카탈로그",
    width: "w-[112px]",
    align: "flex justify-center",
    key: "IS_CATALOGED",
    sortable: true,
  },
];

export const ItemMainTable: React.FC<Props> = ({
  itemPage,
  handleItemNameClick,
  handlePageChange,
  itemSortType,
  handleSortChange,
  refreshData,
}) => {
  const items = itemPage.contents;
  const totalElements = itemPage.totalElements;
  const currentPage = itemPage.currentPage;
  const startIndex = totalElements - (currentPage - 1) * PaginationSize.SIZE_12;
  const [selectedItems, setSelectedItems] = useRecoilState(selectedItemsState);

  useEffect(() => {
    setSelectedItems([]);
  }, [currentPage, setSelectedItems]);

  const validItems = items.filter((item) => item.price > 0);
  const validItemIds = validItems.map((item) => item.id);

  const allValidItemsSelected =
    validItems.length > 0 &&
    validItemIds.every((id) => selectedItems.includes(id));

  const handleSelectAll = () => {
    if (allValidItemsSelected) {
      setSelectedItems([]);
    } else {
      setSelectedItems(validItemIds);
    }
  };

  const getSortState = (header: SortableTableHeader) => {
    if (!header.sortable) return null;

    const { key } = header;
    const currentSortType = itemSortType;

    if (currentSortType === `${key}_ASC`) {
      return "asc";
    } else if (currentSortType === `${key}_DESC`) {
      return "desc";
    } else if (currentSortType === key) {
      return "normal";
    }

    return null;
  };

  const toggleSort = (header: SortableTableHeader) => {
    if (!header.sortable) return;

    const { key } = header;
    const currentState = getSortState(header);

    switch (currentState) {
      case null:
      case "normal":
        handleSortChange(`${key}_ASC` as ItemAdminOrderTypeExpanded);
        break;
      case "asc":
        handleSortChange(`${key}_DESC` as ItemAdminOrderTypeExpanded);
        break;
      case "desc":
        handleSortChange(ItemAdminOrderTypeExpanded.ALL);
        break;
    }
  };

  return (
    <div className="overflow-x-auto border rounded-lg">
      <Table>
        <Table.Head>
          {headers.map((header) => {
            const sortState = getSortState(header);

            return (
              <Table.HeadCell
                key={header.title}
                className={header.width + " h-[52px] p-0 border-b"}
              >
                <div className={header.align}>
                  <CustomCell>
                    {header.title === "선택" ? (
                      <Checkbox
                        checked={allValidItemsSelected}
                        onChange={handleSelectAll}
                        disabled={validItems.length === 0}
                      />
                    ) : (
                      <div
                        className={`flex items-center ${
                          header.sortable ? "cursor-pointer" : ""
                        }`}
                        onClick={() => toggleSort(header)}
                      >
                        <Footnote12Description
                          className={"text-semantic-text-secondary"}
                        >
                          {header.title}
                        </Footnote12Description>
                        {sortState === "asc" && (
                          <ChevronUpOutlineIcon className="w-5 h-5 mt-1 ml-0.5" />
                        )}
                        {sortState === "desc" && (
                          <ChevronDownOutlineIcon className="w-5 h-5 mt-1 ml-0.5" />
                        )}
                        {(sortState === "normal" || sortState === null) &&
                          header.sortable && (
                            <HorizontalLineIcon className="w-5 h-5 mt-1 ml-0.5" />
                          )}
                      </div>
                    )}
                  </CustomCell>
                </div>
              </Table.HeadCell>
            );
          })}
        </Table.Head>
        <Table.Body className="divide-y">
          {items.map((item, index) => (
            <ItemMainTableRow
              key={item.id}
              data={item}
              index={index}
              startIndex={startIndex}
              handleItemNameClick={handleItemNameClick}
              isSelected={selectedItems.includes(item.id)}
              onSelectItem={(id, isSelected) => {
                if (isSelected) {
                  setSelectedItems((prev) => [...prev, id]);
                } else {
                  setSelectedItems((prev) =>
                    prev.filter((itemId) => itemId !== id)
                  );
                }
              }}
            />
          ))}
          <ItemMainTableFooter
            headersLength={headers.length}
            itemPage={itemPage}
            handlePageChange={handlePageChange}
            selectedItems={selectedItems}
            onClearSelection={() => setSelectedItems([])}
            refreshData={refreshData}
          />
        </Table.Body>
      </Table>
    </div>
  );
};
